import { MdSend } from '@react-icons/all-files/md/MdSend'
import isEqual from 'lodash/isEqual'
import * as React from 'react'
import { connect, ConnectedProps } from 'react-redux'
import {
  Link,
  Location,
  Outlet,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import CloseSvg from '!@svgr/webpack!assets/icons/close.svg'

import { EMAIL_CAMPAIGN_BUILDER } from '@disco/constants/FeatureTypes'
import {
  loadContactEmailGroups,
  loadEmailGroup,
  loadEmailGroups,
  loadMailingLists,
} from '@disco/state-management/actions/EmailGroupActions'
import { searchContacts } from '@disco/state-management/actions/contact-actions'
import { updateUserSettingsUnsafe } from '@disco/state-management/actions/ExplorerActions'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import {
  getDeletedEmailGroupIds,
  getEmailGroup,
} from '@disco/state-management/selectors/emailGroups'

import type { RecipientList } from 'types/list'

import DocumentHeadTags from 'components/document-head-tags'
import NotFoundPage from 'components/not-found-page'
import { Spinner } from 'components/ui'
import ContactUsLink from 'components/ui/contact-us-link'

import type { RootState } from 'store/configureStore'

import ListDialog from './list-dialog'
import styles from './index.module.css'

type OwnProps = {
  children: React.ReactNode
  location: Location
  params: {
    listId?: string
    contactId?: string
  }
  searchParams: URLSearchParams
}

function mapStateToProps(state: RootState, props: OwnProps) {
  let listId
  let list: Pick<RecipientList, 'id' | 'name' | 'loading'> | null | undefined =
    null

  if (props.params.listId) {
    listId = parseInt(props.params.listId)
  } else if (props.searchParams.get('append')) {
    listId = parseInt(props.searchParams.get('append'), 10)
  }

  if (listId) {
    list = getEmailGroup(state, {
      listId: listId,
    })
    list = list || {
      id: listId,
      loading: true,
      name: '',
    }
  }

  return {
    settings: state.user.settings,
    emailGroups: state.emailGroups,
    list,
    // a 404 never hydrates the list, so the spinner would otherwise never clear
    isListDeleted: Boolean(
      listId && getDeletedEmailGroupIds(state).includes(listId),
    ),
    campaignsEnabled: isFeatureEnabled(state, {
      featureName: EMAIL_CAMPAIGN_BUILDER,
    }),
  }
}

const mapDispatchToProps = {
  loadEmailGroups,
  loadEmailGroup,
  updateUserSettingsUnsafe,
  loadContactEmailGroups,
  loadMailingLists,
  searchContacts,
}

// Define connector
const connector = connect(mapStateToProps, mapDispatchToProps)

// Infer props type
type PropsFromRedux = ConnectedProps<typeof connector>

// Component props = Redux props + own props
type Props = PropsFromRedux & OwnProps
class Contacts extends React.Component<Props> {
  componentDidMount() {
    const contactId = this.props.params?.contactId ?? this.props.list?.id
    const selectedContactId =
      contactId != null
        ? typeof contactId === 'string'
          ? parseInt(contactId, 10)
          : contactId
        : null

    if (selectedContactId || (!this.props.emailGroups && selectedContactId))
      this.props.loadContactEmailGroups(selectedContactId)
    this.props.loadMailingLists()
    this.props.loadEmailGroups()
    // the full crawl is paged 4 at a time, so fetch this list alone rather than gate the spinner on every list
    if (this.props.list?.loading) this.props.loadEmailGroup(this.props.list.id)
  }
  componentDidUpdate(prevProps: Props) {
    const prevListId = parseInt(prevProps.params?.listId?.toString(), 10)
    const currentListId = parseInt(this.props.params?.listId?.toString(), 10)
    const { emailGroups, mailingList } = this.props.emailGroups

    const prevEmailGroups = prevProps.emailGroups.emailGroups

    const prevRecipients = prevEmailGroups?.flatMap(group => group?.recipients)
    const currentRecipients =
      mailingList && emailGroups?.flatMap(group => group?.recipients)

    const isGroupUpdating = isEqual(prevRecipients, currentRecipients)
    if (currentListId && prevListId !== currentListId && isGroupUpdating) {
      this.props.loadEmailGroup(currentListId)
    }

    // this wrapper stays mounted between /lists and /contacts, so the mount fetch misses in-app ?append= navigation
    const prevAppend = prevProps.searchParams.get('append')
    const append = this.props.searchParams.get('append')
    if (append && append !== prevAppend && this.props.list?.loading) {
      this.props.loadEmailGroup(this.props.list.id)
    }
  }

  componentWillUnmount() {
    // this wrapper stays mounted for all in-section contacts/lists
    // navigation (switching lists, viewing a contact and back), so
    // unmounting means the user actually left the section - clear the
    // search rather than let it resurface on an unrelated later visit
    this.props.searchContacts('', null)
  }

  _handleCloseUpgrade = () => {
    this.props.updateUserSettingsUnsafe({
      settings: {
        dismiss_email_creator_upgrade_2019_02: true,
      },
    })
  }

  render() {
    const { list, isListDeleted, campaignsEnabled } = this.props
    let showUpgrade = !campaignsEnabled

    if ((this.props.settings as any).dismiss_email_creator_upgrade_2019_02) {
      showUpgrade = false
    }

    return (
      <div className="Page">
        <DocumentHeadTags title="Contacts | DISCO" />
        <ListDialog />
        {showUpgrade && (
          <div className={styles.contactsBanner}>
            <MdSend className={styles.contactsBannerIcon} />
            Want a better way to email your contacts?&nbsp;
            <ContactUsLink
              className={styles.contactsBannerLink}
              intercomMsg="I'd like to discuss upgrading to access the Email Creator"
            >
              Upgrade
            </ContactUsLink>
            &nbsp;to access our&nbsp;
            <Link to="/campaigns" className={styles.contactsBannerLink}>
              Email creator
            </Link>
            <button
              className={styles.contactsBannerClose}
              onClick={this._handleCloseUpgrade}
            >
              <CloseSvg />
            </button>
            .
          </div>
        )}
        <div className="Page_Content">
          {isListDeleted ? (
            <NotFoundPage
              subtitle="This mailing list has been deleted."
              body={
                <span>
                  It may have been removed by someone else on your team.
                </span>
              }
            />
          ) : list && list.loading ? (
            <Spinner />
          ) : (
            <Outlet />
          )}
        </div>
      </div>
    )
  }
}

const ConnectedContacts = connector(Contacts)

// Wrapper component to provide router params to the connected component
const ContactsWithRouter = (props: any) => {
  const params = useParams<{ listId?: string }>()
  const [searchParams] = useSearchParams()

  return (
    <ConnectedContacts {...props} params={params} searchParams={searchParams} />
  )
}

export default ContactsWithRouter
